import { Editor } from '../core/Editor';
import { ToolType } from '../types';
import { Icons } from './Icons';

export class ToolBar {
  private editor: Editor;
  private container: HTMLElement;

  // Photoshop standard tool arrangement and naming
  private toolsList: { id: ToolType; name: string; key: string; icon: string }[] = [
    { id: 'move', name: 'Move Tool', key: 'V', icon: Icons.move },
    { id: 'marquee', name: 'Rectangular Marquee Tool', key: 'M', icon: Icons.marquee },
    { id: 'wand', name: 'Magic Wand Tool', key: 'W', icon: Icons.wand },
    { id: 'crop', name: 'Crop Tool', key: 'C', icon: Icons.crop },
    { id: 'eyedropper', name: 'Eyedropper Tool', key: 'I', icon: Icons.eyedropper },
    { id: 'heal', name: 'Spot Healing Brush Tool', key: 'J', icon: Icons.heal },
    { id: 'brush', name: 'Brush Tool', key: 'B', icon: Icons.brush },
    { id: 'eraser', name: 'Eraser Tool', key: 'E', icon: Icons.eraser },
    { id: 'bucket', name: 'Paint Bucket Tool', key: 'G', icon: Icons.bucket },
    { id: 'smudge', name: 'Smudge Tool', key: 'R', icon: Icons.smudge },
    { id: 'bg-remove', name: 'Magic Eraser / Remove Background', key: 'K', icon: Icons.bgRemove },
    { id: 'text', name: 'Horizontal Type Tool', key: 'T', icon: Icons.text },
    { id: 'hand', name: 'Hand Tool', key: 'H', icon: Icons.hand }
  ];

  constructor(editor: Editor, container: HTMLElement) {
    this.editor = editor;
    this.container = container;
    this.render();
    this.setupGlobalDismiss();
  }

  private setupGlobalDismiss() {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.tool-item-wrapper')) {
        this.closeAllFlyouts();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllFlyouts();
      }
    });
  }

  public closeAllFlyouts() {
    this.container.querySelectorAll('.tool-flyout-menu').forEach(menu => {
      menu.classList.remove('open');
    });
  }

  public render() {
    const isWandCutCrop = this.editor.toolSettings.wandMode === 'cut-crop';

    this.container.innerHTML = `
      <div class="toolbar-tools">
        ${this.toolsList
          .map(t => {
            const isWand = t.id === 'wand';
            const icon = isWand
              ? (isWandCutCrop ? Icons.wandCrop : Icons.wand)
              : t.icon;
            const title = isWand
              ? (isWandCutCrop ? 'Magic Wand: Remove BG & Crop (Default - W)' : 'Magic Wand Selection Tool (Shift+W)')
              : `${t.name} (${t.key})`;

            return `
              <div class="tool-item-wrapper" data-tool-wrapper="${t.id}">
                <button type="button" class="tool-btn ${this.editor.toolManager.activeTool.id === t.id ? 'active' : ''}" 
                        data-tool="${t.id}" title="${title}" aria-label="${t.name}">
                  <span class="tool-icon-slot">${icon}</span>
                  <span class="ps-triangle" title="${isWand ? 'Click or right-click for Remove BG & Crop dropdown' : ''}"></span>
                  <span class="tool-hotkey">${t.key}</span>
                </button>

                ${isWand ? `
                  <div class="tool-flyout-menu" id="wand-flyout-menu">
                    <div class="tool-flyout-header">Magic Wand Tools</div>
                    
                    <button type="button" class="tool-flyout-item ${isWandCutCrop ? 'active' : ''}" data-wand-variant="cut-crop" title="Click background to erase it and crop away all border space">
                      <span class="flyout-icon">${Icons.wandCrop}</span>
                      <div class="flyout-info">
                        <div class="flyout-title">
                          <span>Magic Wand: Remove BG & Crop</span>
                          <span class="flyout-badge" style="background: #e0e7ff; color: #4338ca;">DEFAULT</span>
                        </div>
                        <div class="flyout-desc">Erase background & auto-crop canvas</div>
                      </div>
                      <kbd>W</kbd>
                    </button>

                    <button type="button" class="tool-flyout-item ${!isWandCutCrop ? 'active' : ''}" data-wand-variant="select" title="Standard selection tool">
                      <span class="flyout-icon">${Icons.wand}</span>
                      <div class="flyout-info">
                        <div class="flyout-title">
                          <span>Magic Wand Selection Tool</span>
                        </div>
                        <div class="flyout-desc">Standard color flood selection</div>
                      </div>
                      <kbd>Shift+W</kbd>
                    </button>

                    <div class="tool-flyout-divider"></div>

                    <button type="button" class="tool-flyout-item" data-action="trim-borders" title="Auto-crop all transparent borders right now">
                      <span class="flyout-icon">${Icons.trim}</span>
                      <div class="flyout-info">
                        <div class="flyout-title">
                          <span>Trim Transparent Borders</span>
                          <span class="flyout-badge" style="background: #e0f2fe; color: #0284c7;">1-Click</span>
                        </div>
                        <div class="flyout-desc">Crop all empty border space now</div>
                      </div>
                      <kbd>Alt+Shift+T</kbd>
                    </button>
                  </div>
                ` : ''}
              </div>
            `;
          })
          .join('')}
      </div>

      <!-- Photoshop Color Swatches & Switcher -->
      <div class="color-swatches-box">
        <div class="swatches-container">
          <input type="color" id="fg-color-picker" value="${this.editor.toolSettings.brushColor}" class="swatch-input fg-swatch" title="Foreground Color (Click to change)" />
          <input type="color" id="bg-color-picker" value="#ffffff" class="swatch-input bg-swatch" title="Background Color (Click to change)" />
          <button type="button" class="swatch-swap-btn" id="swap-colors-btn" title="Switch Foreground and Background Colors (X)">
            ${Icons.swap}
          </button>
        </div>
        <button type="button" class="swatch-reset-btn" id="reset-colors-btn" title="Default Foreground and Background Colors (D)">
          <div class="mini-d-box" title="Default B&W (D)">
            <div class="d-fg"></div>
            <div class="d-bg"></div>
          </div>
        </button>
      </div>
    `;

    // Tool click events
    this.container.querySelectorAll('.tool-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const toolId = target.dataset.tool as ToolType;
        const isCurrentActive = this.editor.toolManager.activeTool.id === toolId;

        if (toolId === 'wand') {
          // If already active wand, toggle the flyout menu
          const flyout = this.container.querySelector('#wand-flyout-menu') as HTMLElement;
          if (isCurrentActive) {
            flyout?.classList.toggle('open');
          } else {
            this.editor.setTool('wand');
            this.closeAllFlyouts();
          }
        } else {
          this.closeAllFlyouts();
          if (toolId) {
            this.editor.setTool(toolId);
          }
        }
      });

      // Right click to open flyout menu for wand
      btn.addEventListener('contextmenu', (e) => {
        const target = e.currentTarget as HTMLElement;
        const toolId = target.dataset.tool as ToolType;
        if (toolId === 'wand') {
          e.preventDefault();
          this.editor.setTool('wand');
          const flyout = this.container.querySelector('#wand-flyout-menu') as HTMLElement;
          flyout?.classList.toggle('open');
        }
      });
    });

    // Triangle click explicitly opens flyout for wand
    const wandTriangle = this.container.querySelector('[data-tool-wrapper="wand"] .ps-triangle');
    wandTriangle?.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.editor.setTool('wand');
      const flyout = this.container.querySelector('#wand-flyout-menu') as HTMLElement;
      flyout?.classList.toggle('open');
    });

    // Flyout menu item clicks
    const wandFlyout = this.container.querySelector('#wand-flyout-menu');
    if (wandFlyout) {
      wandFlyout.querySelectorAll('.tool-flyout-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const target = e.currentTarget as HTMLElement;
          const variant = target.dataset.wandVariant as 'select' | 'cut-crop' | undefined;
          const action = target.dataset.action;

          if (variant) {
            this.editor.setWandMode(variant);
            this.editor.setTool('wand');
            this.updateWandIcon();
            this.closeAllFlyouts();

            if (variant === 'cut-crop') {
              this.editor.showToast('✂️ Magic Wand: Remove BG & Crop active — click background to erase & crop');
            } else {
              this.editor.showToast('🪄 Magic Wand: Selection mode active');
            }
          } else if (action === 'trim-borders') {
            this.closeAllFlyouts();
            this.editor.trimTransparentBorders();
          }
        });
      });
    }

    // Foreground color picker
    const fgPicker = this.container.querySelector('#fg-color-picker') as HTMLInputElement;
    fgPicker?.addEventListener('input', () => {
      this.editor.setForegroundColor(fgPicker.value);
    });

    // Color swap (X)
    this.container.querySelector('#swap-colors-btn')?.addEventListener('click', () => {
      this.editor.swapColors();
      const bgPicker = this.container.querySelector('#bg-color-picker') as HTMLInputElement;
      if (fgPicker && bgPicker) {
        fgPicker.value = this.editor.toolSettings.brushColor;
      }
    });

    // Reset default black & white (D)
    this.container.querySelector('#reset-colors-btn')?.addEventListener('click', () => {
      this.editor.resetColors();
      const bgPicker = this.container.querySelector('#bg-color-picker') as HTMLInputElement;
      if (fgPicker) fgPicker.value = '#000000';
      if (bgPicker) bgPicker.value = '#ffffff';
    });
  }

  public updateWandIcon() {
    const wandWrapper = this.container.querySelector('[data-tool-wrapper="wand"]');
    if (!wandWrapper) return;
    const isCutCrop = this.editor.toolSettings.wandMode === 'cut-crop';
    const iconSlot = wandWrapper.querySelector('.tool-icon-slot');
    const btn = wandWrapper.querySelector('.tool-btn') as HTMLElement;

    if (iconSlot) {
      iconSlot.innerHTML = isCutCrop ? Icons.wandCrop : Icons.wand;
    }
    if (btn) {
      btn.title = isCutCrop ? 'Magic Wand: Remove BG & Crop (Shift+W)' : 'Magic Wand Tool (W)';
    }

    wandWrapper.querySelectorAll('.tool-flyout-item').forEach(item => {
      const el = item as HTMLElement;
      const v = el.dataset.wandVariant;
      if (v) {
        el.classList.toggle('active', (v === 'cut-crop') === isCutCrop);
      }
    });
  }

  public setActiveTool(toolId: ToolType) {
    this.container.querySelectorAll('.tool-btn').forEach(btn => {
      const b = btn as HTMLElement;
      b.classList.toggle('active', b.dataset.tool === toolId);
    });
    this.updateWandIcon();
  }

  public updateFgColor(color: string) {
    const fgPicker = this.container.querySelector('#fg-color-picker') as HTMLInputElement;
    if (fgPicker) fgPicker.value = color;
  }
}
